import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Box, Typography, TextField, FormControlLabel, Switch, Divider, Stack, Alert, Tooltip } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { neutral } from '_lex/_foundations/phxColors';
import { TemplateField, TemplateSection, SectionFieldOption, Template } from '../../_types';
import DefaultValuePicker from './DefaultValuePicker';
import useTranslations from 'hooks/useTranslations';
import { getFieldInheritanceType, isFieldLockedByParent } from '../../_utils/fieldComparison';
import FieldInheritanceBadge from './FieldInheritanceBadge';
import { useTemplateBuilderContext } from '../context';

interface FieldPropertiesPanelProps {
  field: TemplateField;
  section: TemplateSection;
  parentTemplate?: Template | null;
  currentSection?: TemplateSection;
  onLabelChange: (fieldId: number, label: string) => void;
  onDefaultValueChange: (fieldId: number, defaultValues: string[]) => void;
  onRequiredChange: (fieldId: number, required: boolean) => void;
  onLockedChange: (fieldId: number, locked: boolean) => void;
  onOptionsChange?: (fieldId: number, options: SectionFieldOption[]) => void;
  trackUserActivity?: () => void;
  isChildTemplate?: boolean;
}

const FieldPropertiesPanel: React.FC<FieldPropertiesPanelProps> = ({
  field,
  section,
  parentTemplate,
  currentSection,
  onLabelChange,
  onDefaultValueChange,
  onRequiredChange,
  onLockedChange,
  onOptionsChange,
  trackUserActivity,
  isChildTemplate = false,
}) => {
  const t = useTranslations();
  const { template, isLexipolTemplate } = useTemplateBuilderContext();
  const templateMode = isLexipolTemplate ? 'lexipol' : 'agency';

  // Check if field is locked by parent (direct or via section cascade)
  const isLockedByParent = isFieldLockedByParent(field, section, parentTemplate);

  // Use effectiveLabel for display
  const [localLabel, setLocalLabel] = useState(field?.effectiveLabel || field?.name || '');
  useEffect(() => {
    const newLabel = field?.effectiveLabel || field?.name || '';
    setLocalLabel(newLabel);
  }, [field?.id, field?.effectiveLabel]);

  // Use hybrid identifier: field.id if saved (id !== 0), otherwise use field.fieldId for unsaved fields
  const fieldIdentifier = field.id === 0 ? field.fieldId : field.id;

  const handleLabelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setLocalLabel(newValue);

    onLabelChange(fieldIdentifier, newValue);
  };

  const handleDefaultValueChange = useCallback(
    (value: string[]) => {
      onDefaultValueChange(fieldIdentifier, value);
    },
    [fieldIdentifier, onDefaultValueChange],
  );

  const handleOptionsChange = useCallback(
    (options: SectionFieldOption[]) => {
      if (onOptionsChange) {
        onOptionsChange(fieldIdentifier, options);
      }
    },
    [fieldIdentifier, onOptionsChange],
  );

  const handleRequiredChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onRequiredChange(fieldIdentifier, event.target.checked);
  };

  const handleLockedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onLockedChange(fieldIdentifier, event.target.checked);
  };

  // Determine if this field has a parent and what its inheritance status is
  // Use parent template from context instead of parentSectionField
  const inheritanceType = isChildTemplate ? getFieldInheritanceType(field, template.parentTemplate) : null;

  return (
    <Box sx={{ height: '100%', overflow: 'auto', p: 2, maxWidth: '100%' }}>
      <Stack spacing={3}>
        {/* Yellow banner for locked fields */}
        {isLockedByParent && (
          <Alert
            severity='warning'
            icon={<LockIcon fontSize='small' />}
            sx={{
              '& .MuiAlert-icon': {
                color: '#ed6c02',
              },
              '& .MuiAlert-message': {
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'normal',
              },
            }}
          >
            {t(`performanceReporting.templates.builder.${templateMode}.lockedField.banner`)}
          </Alert>
        )}

        {/* Field Inheritance Badge - only show for child template fields */}
        {inheritanceType && (
          <Box>
            <FieldInheritanceBadge
              type={inheritanceType}
              organizationId={template.organizationId}
            />
          </Box>
        )}

        {/* Label */}
        <Box>
          <Typography
            variant='body2'
            fontWeight='600'
            color='text.primary'
            sx={{ mb: 1 }}
          >
            Label
          </Typography>
          <TextField
            fullWidth
            size='small'
            value={localLabel}
            onChange={handleLabelChange}
            variant='outlined'
            error={!localLabel.trim()}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
                fontSize: '0.875rem',
              },
            }}
          />
          {!localLabel.trim() && (
            <Alert
              severity='warning'
              sx={{
                mt: 1,
                fontSize: '0.75rem',
                '& .MuiAlert-message': {
                  fontSize: '0.75rem',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  whiteSpace: 'normal',
                },
              }}
            >
              {t('performanceReporting.templates.builder.fieldProperties.validation.emptyLabel')}
            </Alert>
          )}
        </Box>
        {!['Attachment', 'BodyImage', 'VehicleImage', 'Narrative'].includes(field.fieldType) && (
          <Box sx={{ maxWidth: '100%' }}>
            <Typography
              variant='body2'
              fontWeight='600'
              color='text.primary'
              sx={{ mb: 1 }}
            >
              {(field.fieldType === 'SingleSelect' || field.fieldType === 'MultiSelect') ? '' : 'Default Value'}
            </Typography>
            <DefaultValuePicker
              key={field.id}
              field={field}
              value={field?.effectiveDefaultValues ?? []}
              onChange={handleDefaultValueChange}
              onOptionsChange={handleOptionsChange}
              trackUserActivity={trackUserActivity}
              disabled={isLockedByParent}
            />
          </Box>
        )}

        <Divider />

        {/* Required field toggle */}
        <Box>
          <Tooltip
            title={t(`performanceReporting.templates.builder.${templateMode}.lockedField.inheritedAttribute`)}
            disableHoverListener={!isLockedByParent}
            placement='top'
          >
            <FormControlLabel
              control={
                <Switch
                  checked={field.effectiveIsRequired || false}
                  onChange={handleRequiredChange}
                  size='small'
                  disabled={isLockedByParent}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#4caf50', // Green color like the rest of the app
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#4caf50', // Green track when enabled
                    },
                  }}
                />
              }
              label={
                <Box>
                  <Typography
                    variant='body2'
                    fontWeight='600'
                    color='text.primary'
                  >
                    Required field
                  </Typography>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      hyphens: 'auto',
                    }}
                  >
                    Field must be filled out.
                  </Typography>
                </Box>
              }
              labelPlacement='start'
              sx={{
                m: 0,
                width: '100%',
                justifyContent: 'space-between',
                '& .MuiFormControlLabel-label': {
                  flex: 1,
                  maxWidth: 'calc(100% - 60px)', // Account for switch width
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                },
              }}
            />
          </Tooltip>
        </Box>

        {/* Locked field toggle - only show for Lexipol templates */}
        {isLexipolTemplate && (
          <Box>
            <Tooltip
              title={t(`performanceReporting.templates.builder.${templateMode}.lockedField.inheritedAttribute`)}
              disableHoverListener={!isLockedByParent}
              placement='top'
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={field.effectiveIsLocked || false}
                    onChange={handleLockedChange}
                    size='small'
                    disabled={isLockedByParent}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#4caf50', // Green color like the rest of the app
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#4caf50', // Green track when enabled
                      },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography
                      variant='body2'
                      fontWeight='600'
                      color='text.primary'
                    >
                      Locked field
                    </Typography>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        hyphens: 'auto',
                      }}
                    >
                      Prevent downstream overrides.
                    </Typography>
                  </Box>
                }
                labelPlacement='start'
                sx={{
                  m: 0,
                  width: '100%',
                  justifyContent: 'space-between',
                  '& .MuiFormControlLabel-label': {
                    flex: 1,
                    maxWidth: 'calc(100% - 60px)', // Account for switch width
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                  },
                }}
              />
            </Tooltip>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default FieldPropertiesPanel;
